import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../App';
import { NavBar } from '../components/NavBar';
import { useSound } from '../hooks/useSound';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight } from 'lucide-react';
import { CurriculumMapButton } from '../components/CurriculumMap';
import axios from 'axios';

const LEVEL_LABELS = {
  'pre-A1': 'Beginner',
  'A1': 'Elementary',
  'A1-A2': 'Intermediate',
  'A2': 'Upper Intermediate',
};

const LEVEL_LABEL_KEYS = {
  'pre-A1': 'cefr.beginner',
  'A1': 'cefr.elementary',
  'A1-A2': 'cefr.intermediate',
  'A2': 'cefr.upperIntermediate',
};

const TOPIC_COLORS = [
  '#E03131', '#1971C2', '#2F9E44', '#7048E8', '#F59F00',
  '#E8590C', '#0B7285', '#862E9C', '#D6336C', '#5C940D',
  '#1098AD', '#845EF7', '#E64980', '#15AABF', '#FF6B6B',
  '#4C6EF5', '#20C997',
];

export function LevelTopics() {
  const { level } = useParams();
  const decodedLevel = decodeURIComponent(level);
  const [topics, setTopics] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();
  const { playSound } = useSound();
  const { t } = useLanguage();

  const levelLabel = t(LEVEL_LABEL_KEYS[decodedLevel]) || LEVEL_LABELS[decodedLevel] || decodedLevel;

  useEffect(() => {
    axios.get(`${API}/levels/${encodeURIComponent(decodedLevel)}/topics`).then(r => {
      setTopics(r.data);
      setTotalItems(r.data.reduce((sum, t) => sum + t.item_count, 0));
    }).catch(console.error);
  }, [decodedLevel]);

  return (
    <div data-testid="level-topics-screen" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <NavBar
        items={[levelLabel]}
        onBack={() => navigate('/browse')}
      />
      <div style={{ flex: 1, padding: '1.5rem 2rem', overflow: 'auto' }}>
        <h2 style={{
          fontFamily: 'Fredoka, sans-serif', fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
          fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center', marginBottom: '0.5rem',
        }}>
          {levelLabel} ({decodedLevel})
        </h2>
        <p style={{ textAlign: 'center', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
          {t('page.chooseTopic')} {totalItems.toLocaleString()} {t('page.items')}
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
          <CurriculumMapButton
            queryParams={{ mode: 'level', level: decodedLevel }}
            title={`${levelLabel} (${decodedLevel}) — ${t('page.curriculumAlignment')}`}
          />
        </div>

        {/* "All Topics" card */}
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <button
            data-testid="level-all-topics-button"
            onClick={() => {
              playSound('click');
              navigate(`/games?mode=level&level=${encodeURIComponent(decodedLevel)}`);
            }}
            style={{
              width: '100%', padding: '1.25rem 1.5rem', marginBottom: '1rem',
              background: 'var(--primary)', color: 'white', border: 'none',
              borderRadius: '1.25rem', borderBottom: '6px solid #3B5BDB',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              fontFamily: 'Fredoka, sans-serif', fontSize: '1.3rem', fontWeight: 700,
              boxShadow: '0 4px 16px rgba(76,110,245,0.25)',
            }}
          >
            <span>{t('page.allTopics')} &mdash; {totalItems.toLocaleString()} {t('page.items')}</span>
            <ArrowRight size={24} />
          </button>

          {/* Topic cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: '1rem',
          }}>
            {topics.map((tp, i) => (
              <button key={tp.topic} data-testid={`level-topic-${tp.topic}-button`}
                onClick={() => {
                  playSound('click');
                  navigate(`/games?mode=level&level=${encodeURIComponent(decodedLevel)}&topic=${tp.topic}`);
                }}
                style={{
                  background: 'var(--surface)', border: '3px solid var(--border)',
                  borderRadius: '1.25rem', padding: '1.25rem', cursor: 'pointer',
                  textAlign: 'left', transition: 'all 0.15s',
                  borderLeft: `6px solid ${TOPIC_COLORS[i % TOPIC_COLORS.length]}`,
                  animation: `fade-in 0.3s ease ${i * 0.03}s both`,
                }}
              >
                <div style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                  {t(`topic.${tp.topic}`) !== `topic.${tp.topic}` ? t(`topic.${tp.topic}`) : tp.label}
                </div>
                <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                  {tp.item_count} {t('page.items')} &middot; {tp.game_count} {t('page.games')}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
